import { type FC, useState, useCallback } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { orderFeedbackEvent } from '../tracking/orderFeedbackTracking.ts';
import { graphql, createFragmentContainer } from 'react-relay/legacy.js';
import { PdpSharedSellerDetailsModalLazy } from '../shared/PdpSharedSellerDetailsModal/PdpSharedSellerDetailsModalLazy.tsx';

// components
import { Link } from 'dibs-elements/exports/Link.tsx';

// types
import { type PdpSharedDealerDetailsFeedback_seller$data as Seller } from './__generated__/PdpSharedDealerDetailsFeedback_seller.graphql.ts';

// styles
import styles from './PdpSharedDealerDetailsFeedback.scss';

type Props = {
    seller: Seller;
    showSellerBranding?: boolean;
};

const PdpSharedDealerDetailsFeedbackComponent: FC<Props> = ({ seller, showSellerBranding }) => {
    const { orderFeedbackPdp: reviews, serviceId } = seller || {};
    const numberOfReviews = reviews?.metadata?.aggregatedFeedback?.publicNoteTotalResults;
    const [isReviewsModalOpen, setIsReviewModalOpen] = useState(false);

    const openModal = useCallback(() => {
        orderFeedbackEvent({
            action: 'seller reviews expanded',
            label: 'pdp - seller details',
            count: numberOfReviews || 0,
        });
        setIsReviewModalOpen(true);
    }, [numberOfReviews]);

    const onCloseModal = useCallback(() => {
        setIsReviewModalOpen(false);
    }, []);

    if (!numberOfReviews) {
        return null;
    }

    return (
        <>
            <Link
                onClick={openModal}
                className={styles.reviewsLink}
                dataTn="order-feedback-modal-trigger"
            >
                <FormattedMessage
                    id="PdpSharedDealerDetailsFeedback.seeReviews"
                    defaultMessage="{numberOfReviews, plural, one {# Review} other {# Reviews}}"
                    values={{ numberOfReviews }}
                />
            </Link>

            <PdpSharedSellerDetailsModalLazy
                sellerId={serviceId}
                isOpen={isReviewsModalOpen}
                onClose={onCloseModal}
                showSellerBranding={showSellerBranding}
            />
        </>
    );
};

export const PdpSharedDealerDetailsFeedback = createFragmentContainer(
    PdpSharedDealerDetailsFeedbackComponent,
    {
        seller: graphql`
            fragment PdpSharedDealerDetailsFeedback_seller on Seller {
                serviceId
                orderFeedbackPdp {
                    metadata {
                        aggregatedFeedback {
                            publicNoteTotalResults
                        }
                    }
                }
            }
        `,
    }
);
